import type { EventItem, LoggedDose, Reading } from "@/types";

/**
 * Readings, doses and events, interleaved into the order they happened.
 *
 * They belong in one list because they are one story: a reading is what the
 * water was, a dose is what you did about it, an event is what happened to it.
 * Kept apart, the chart shows FC jumping 6 ppm overnight with no visible cause
 * and you are left trying to remember whether you poured something in — or
 * whether it rained.
 */
export type TimelineEntry =
  | { kind: "reading"; at: string; reading: Reading }
  | { kind: "doses"; at: string; doses: LoggedDose[] }
  //: `phase` distinguishes the two ends of a span. A point event is "start".
  | { kind: "event"; at: string; event: EventItem; phase: "start" | "end" };

/**
 * Doses sharing an exact timestamp were one application — the batch form sends
 * a single `dosed_at` for everything added on one trip to the pool. Grouping on
 * exact equality is deliberate: doses logged one-tap from separate suggestions
 * carry their own `new Date()` and differ by milliseconds, so they stay
 * separate, which is correct. They were separate decisions.
 */
export function buildTimeline(
  readings: Reading[],
  doses: LoggedDose[],
  events: EventItem[] = [],
): TimelineEntry[] {
  const batches = new Map<string, LoggedDose[]>();
  for (const dose of doses) {
    const existing = batches.get(dose.dosed_at);
    if (existing) existing.push(dose);
    else batches.set(dose.dosed_at, [dose]);
  }

  const entries: TimelineEntry[] = [
    ...readings.map((reading): TimelineEntry => ({
      kind: "reading",
      at: reading.taken_at,
      reading,
    })),
    ...[...batches.entries()].map(([at, group]): TimelineEntry => ({
      kind: "doses",
      at,
      doses: group,
    })),
  ];

  for (const event of events) {
    entries.push({ kind: "event", at: event.starts_at, event, phase: "start" });
    // A closed span gets an entry at each end. Otherwise a season opened in May
    // and closed in September appears only in May, and scrolling to September
    // shows nothing happened — when in fact the pool was closed that day.
    if (event.is_span && event.ends_at) {
      entries.push({ kind: "event", at: event.ends_at, event, phase: "end" });
    }
  }

  // Newest first. Ties break reading, then dose, then event: you test before
  // you treat, so a dose above its own reading reads as having dosed blind.
  // Beyond that the order is arbitrary but fixed, so the list does not reshuffle
  // between renders.
  const RANK = { reading: 0, doses: 1, event: 2 };
  return entries.sort((a, b) => {
    const delta = new Date(b.at).getTime() - new Date(a.at).getTime();
    if (delta !== 0) return delta;
    return RANK[a.kind] - RANK[b.kind];
  });
}

/** "1 gal liquid chlorine", the way you would say it aloud. */
export function describeDose(dose: LoggedDose): string {
  const amount = Number(dose.amount);
  const rounded = Number.isFinite(amount)
    ? String(Number(amount.toFixed(2)))
    : dose.amount;
  return `${rounded} ${unitLabel(dose.unit)} ${dose.chemical_label.toLowerCase()}`;
}

/** How a unit is said where space is short. `oz` is weight and `fl oz` is
 * volume — a factor of 16 on a powder, so the distinction stays visible. */
export function unitLabel(unit: string): string {
  return UNIT_LABELS[unit] ?? unit;
}

/** What the dose was expected to do, for the parameters worth showing. */
export function describeEffect(dose: LoggedDose): string | null {
  const effect = dose.predicted_effect;
  if (!effect) return null;

  const parts = Object.entries(effect)
    .map(([key, raw]) => {
      const value = Number(raw);
      if (!Number.isFinite(value) || value === 0) return null;
      const label = key === "ph" ? "pH" : key.toUpperCase();
      // pH moves in hundredths; ppm figures in tenths. Showing pH to one
      // decimal renders every acid dose as "-0.5" regardless of size.
      const shown = key === "ph" ? value.toFixed(2) : value.toFixed(1);
      return `${label} ${value > 0 ? "+" : ""}${shown}`;
    })
    .filter((part): part is string => part !== null);

  return parts.length > 0 ? parts.join(" · ") : null;
}

const UNIT_LABELS: Record<string, string> = {
  gal: "gal",
  fl_oz: "fl oz",
  lb: "lb",
  oz: "oz",
  tab: "tabs",
};


/** "Rain", "Drain and refill" — the event type as a person would say it. */
export function eventLabel(event: EventItem, phase: "start" | "end"): string {
  const base = LABELS[event.event_type] ?? event.event_type.replace(/_/g, " ");
  if (!event.is_span) return base;
  return phase === "start" ? `${base} — started` : `${base} — ended`;
}

/** The payload fields worth reading in a list, rendered compactly.
 *
 * Generic over the payload rather than switched per event type: the payloads
 * are a discriminated union that grows, and a switch here would silently omit
 * whatever was added last. `event_type` is dropped because it is the
 * discriminator, already shown as the label.
 */
export function eventDetail(event: EventItem): string | null {
  const parts = Object.entries(event.payload ?? {})
    .filter(([key, value]) => key !== "event_type" && value !== null && value !== "")
    .map(([key, value]) => `${key.replace(/_/g, " ")} ${value}`);

  if (event.is_open) parts.push("still open");
  return parts.length > 0 ? parts.join(" · ") : null;
}

const LABELS: Record<string, string> = {
  rain: "Rain",
  soak: "Soak",
  drain_refill: "Drain and refill",
  backwash: "Backwash",
  season_open: "Season open",
  season_close: "Season close",
  solar_cover: "Solar cover",
  equipment_change: "Equipment change",
  liner_replacement: "Liner replacement",
  other: "Other",
};
